<template>
  <div></div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useFileUploader } from "@plugins/file-uploader-v2/composables/useFileUploader.ts";
import { onMounted } from "vue";
import {
  FileResponse,
  FileStatus,
  FileUploadEntry,
  UploadFlowValues,
} from "@plugins/file-uploader-v2/types.ts";

interface FileUploaderParams {
  uploadFlow: UploadFlowValues;
  fileEntry?: FileUploadEntry;
  /**
   * Type of the owning resource ("UGC" or "MO").
   * Must be set so the lambda stores it in Valkey at generate-scan-url time,
   * enabling the BE FileMoveJobListenerImpl to route the file correctly after scan.
   */
  recordType?: string | undefined;
  /**
   * UUID of the owning resource (ugcUuid or moUuid).
   * Must be set alongside recordType — both come from the create API call
   * that the caller makes BEFORE showing FileUploaderHidden.
   */
  recordUuid?: string | undefined;
}

// thru modal param
interface Props {
  params?: FileUploaderParams;
}

const props = withDefaults(defineProps<Props>(), {
  params: () => ({
    uploadFlow: "" as UploadFlowValues,
    fileEntry: undefined,
    recordType: undefined,
    recordUuid: undefined,
  }),
});

const params = props.params || {};

const emit = defineEmits(["update:fileEntry"]);

const uploadFlow = computed(() => {
  return params?.uploadFlow;
});

const recordType = computed(() => params?.recordType);
const recordUuid = computed(() => params?.recordUuid);

const fileEntry = ref<FileUploadEntry | null>(params.fileEntry || null);
const emitEvent = (data: FileResponse) => {
  emit("update:fileEntry", data);
};

const { validateFile, uploadFile } = useFileUploader(
  uploadFlow,
  emitEvent,
  fileEntry,
  recordType,
  recordUuid,
);

onMounted(async () => {
  if (!params.fileEntry) return;

  try {
    const isValid = await validateFile(params.fileEntry);
    if (isValid && fileEntry.value) {
      fileEntry.value.isValid = true;
      await uploadFile();
    }
  } catch {
    // Keep the caller's upload state in sync when an unexpected validation or upload error escapes
    // the composable; otherwise a recorder can remain in its loading state indefinitely.
    emitEvent({ status: FileStatus.FAILED });
  }
});
</script>
